import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { logout } from '../store/authSlice';
import { useAppDispatch } from './useAppDispatch';
import notificationService from '../services/notificationService';
import smartSearchService from '../services/smartSearchService';
import secureStorage from '../utils/secureStorage';
import SecureTokenUtils from '../utils/secureTokenUtils';

export const useSessionMonitor = () => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const dispatch = useAppDispatch();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasShownWarning = useRef(false);

  useEffect(() => {
    const checkTokenExpiration = () => {
      const token = secureStorage.getToken();
      
      if (!token || !isAuthenticated) {
        return;
      }

      // Optional preflight validation is disabled by default.
      // Rely on API 401 + refresh flow handled in axios interceptors.
      if (process.env.REACT_APP_TOKEN_PREFLIGHT === 'true') {
        SecureTokenUtils.isTokenExpired(token).then((isExpired) => {
          if (isExpired) {
            notificationService.sessionExpired();
            dispatch(logout());
            smartSearchService.cleanupSession();
            setTimeout(() => {
              window.location.href = '/login?expired=true';
            }, 1000);
          }
        });
      }

      // Disable noisy client-side expiring-soon warnings by default.
      // You can re-enable this with REACT_APP_TOKEN_PREFLIGHT=true.
    };

    if (isAuthenticated) {
      // Check immediately
      checkTokenExpiration();
      
      // Set up periodic check every minute
      intervalRef.current = setInterval(checkTokenExpiration, 60000);
    } else {
      // Reset warning flag when not authenticated
      hasShownWarning.current = false;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, dispatch]);

  // Reset warning flag when user makes a successful request (token is refreshed)
  const resetWarning = () => {
    hasShownWarning.current = false;
  };

  return { resetWarning };
};

export default useSessionMonitor;